import React, { useState, useEffect } from 'react';
import Layout from '../components/common/Layout';
import { csvImportService, CSVImportResult } from '../services/csvImportService';
import { CSVImport as CSVImportType } from '../types';
import { FiUpload, FiDownload, FiFileText, FiCheckCircle, FiXCircle, FiClock, FiAlertCircle } from 'react-icons/fi';
import Icon from '../components/common/Icon';

const CSVImport: React.FC = () => {
  const [importType, setImportType] = useState<'leads' | 'tenders' | 'customers'>('leads');
  const [updateExisting, setUpdateExisting] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<CSVImportResult | null>(null);
  const [error, setError] = useState('');
  const [importHistory, setImportHistory] = useState<CSVImportType[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    loadImportHistory();
  }, []);

  const loadImportHistory = async () => {
    try {
      setLoadingHistory(true);
      const data = await csvImportService.getAll();
      setImportHistory(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load import history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setError('');
      setImportResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await csvImportService.downloadTemplate(importType);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to download template');
    }
  };

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      setImporting(true);
      setError('');
      const result = await csvImportService.import(selectedFile, importType, updateExisting);
      setImportResult(result);
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('csv-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      loadImportHistory();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to import CSV file');
    } finally {
      setImporting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Icon component={FiCheckCircle} className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <Icon component={FiXCircle} className="w-5 h-5 text-red-600" />;
      case 'processing':
        return <Icon component={FiClock} className="w-5 h-5 text-yellow-600 animate-spin" />;
      default:
        return <Icon component={FiClock} className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">CSV Import</h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Import Form */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Import Data</h2>
          <form onSubmit={handleImport} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Import Type *</label>
              <select
                value={importType}
                onChange={(e) => {
                  setImportType(e.target.value as 'leads' | 'tenders' | 'customers');
                  setSelectedFile(null);
                  setImportResult(null);
                }}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="leads">Data</option>
                <option value="tenders">Tenders</option>
                <option value="customers">Customers</option>
              </select>
              {importType === 'leads' && (
                <p className="mt-2 text-sm text-gray-600">
                  File format matches Data export. Include ID for existing lead (assignment only, or base data if “Update existing” is Yes). assignment_id present = update that assignment; assignment_id empty = add or update assignment for that lead+brand+user (no duplicate tuple). New rows without ID create lead and assignment.
                </p>
              )}
              {importType === 'tenders' && (
                <p className="mt-2 text-sm text-gray-600">
                  File format matches Tenders export. Include ID for an existing tender; assignment_id updates that row when it belongs to the tender. Empty assignment_id matches by tender + brand + user. Dates accept several formats (including d/m/Y from export); missing or invalid dates default to today on new tenders and are left unchanged on updates when the date cell is empty. New rows without ID create a tender and assignment.
                </p>
              )}
              {importType === 'customers' && (
                <p className="mt-2 text-sm text-gray-600">
                  Use Export customers as template. Leave ID and Location ID empty for new customer/location; keep them to update existing.
                </p>
              )}
            </div>

            {(importType === 'customers' || importType === 'leads' || importType === 'tenders') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Update existing records</label>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${!updateExisting ? 'text-gray-900' : 'text-gray-500'}`}>No</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={updateExisting}
                    onClick={() => setUpdateExisting(!updateExisting)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${updateExisting ? 'bg-indigo-600' : 'bg-gray-200'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${updateExisting ? 'translate-x-5' : 'translate-x-1'}`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${updateExisting ? 'text-gray-900' : 'text-gray-500'}`}>Yes</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {importType === 'customers'
                    ? 'Yes: update matched customers/locations. No: only insert new records.'
                    : importType === 'leads'
                      ? 'Yes: when row has ID, update lead base data and add/update assignment. No: when row has ID, only add/update assignment (no base data change).'
                      : 'Yes: when row has tender ID, update tender fields and add/update assignment. No: when row has ID, only add/update assignment (no tender field changes).'}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CSV File *</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  id="csv-file"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="block w-full sm:flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium flex items-center gap-2 whitespace-nowrap"
                >
                  <Icon component={FiDownload} className="w-4 h-4" />
                  Download Template
                </button>
              </div>
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                  <Icon component={FiFileText} className="w-4 h-4" />
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>

            {importResult && (
              <div className={`p-4 rounded-lg border ${
                importResult.failed_rows > 0 && importResult.successful_rows === 0
                  ? 'bg-red-50 border-red-200'
                  : importResult.failed_rows > 0
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-green-50 border-green-200'
              }`}>
                <div className="flex items-start gap-3">
                  {importResult.failed_rows > 0 && importResult.successful_rows === 0 ? (
                    <Icon component={FiXCircle} className="w-5 h-5 text-red-600 mt-0.5" />
                  ) : importResult.failed_rows > 0 ? (
                    <Icon component={FiAlertCircle} className="w-5 h-5 text-yellow-600 mt-0.5" />
                  ) : (
                    <Icon component={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">
                      {importResult.failed_rows > 0 && importResult.successful_rows === 0
                        ? 'Import Failed'
                        : importResult.failed_rows > 0
                        ? 'Import Completed with Errors'
                        : 'Import Successful'}
                    </h3>
                    <div className="text-sm space-y-1">
                      <p>Total Rows: {importResult.total_rows}</p>
                      <p className="text-green-700">Successful: {importResult.successful_rows}</p>
                      {importResult.failed_rows > 0 && (
                        <p className="text-red-700">Failed: {importResult.failed_rows}</p>
                      )}
                      {(importResult.customers_created !== undefined || importResult.customers_updated !== undefined) && (
                        <p className="text-gray-700 mt-1">
                          Customers: {importResult.customers_created ?? 0} created, {importResult.customers_updated ?? 0} updated.
                          Locations: {importResult.locations_created ?? 0} created, {importResult.locations_updated ?? 0} updated.
                        </p>
                      )}
                    </div>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-3 max-h-40 overflow-y-auto">
                        <p className="text-xs font-semibold mb-1">Errors:</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          {importResult.errors.slice(0, 10).map((error, index) => (
                            <li key={index} className="text-red-700">{error}</li>
                          ))}
                          {importResult.errors.length > 10 && (
                            <li className="text-gray-600">... and {importResult.errors.length - 10} more errors</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!selectedFile || importing}
                className="px-4 py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-bold"
              >
                <Icon component={FiUpload} className="w-4 h-4" />
                {importing ? 'Importing...' : 'Import CSV'}
              </button>
            </div>
          </form>
        </div>

        {/* Import History */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Import History</h2>
          {loadingHistory ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : importHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No import history</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Success</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Imported By</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {importHistory.map((importRecord) => (
                    <tr key={importRecord.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {new Date(importRecord.import_date).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {importRecord.import_type}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {importRecord.file_name}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {importRecord.total_rows}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-green-600">
                        {importRecord.successful_rows}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-red-600">
                        {importRecord.failed_rows}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(importRecord.status)} flex items-center gap-1 w-fit`}>
                          {getStatusIcon(importRecord.status)}
                          {importRecord.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {importRecord.imported_by_name || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CSVImport;




